import React, { createContext, type ReactNode, useContext, useMemo, useReducer } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { DEFAULT_PANEL_WIDTH, DEFAULT_PANEL_WIDTH_WITH_CHAT_PANEL_LAYOUT } from './constants';
import { StateContext } from './state-context';
import type {
	EntryPointPanel,
	OpenPanelOptions,
	Panel,
	PanelAction,
	PanelSystemState,
} from './types';

/**
 * Context for accessing the panel manager functions.
 * Provides a stable reference that doesn't cause re-renders on state changes.
 * @internal
 */
export const ManagerContext: React.Context<PanelManager | null> =
	createContext<PanelManager | null>(null);

/**
 * Panel manager providing action methods for panel operations
 * @internal
 */
export type PanelManager = {
	openPanel: (options: OpenPanelOptions) => void;
	closePanel: (instanceId: string) => void;
	saveEntryPointReference: (instanceId: string, entryPointReference: any) => void;
	/**
	 * Updates the requested panel width stored in provider state, in pixels.
	 *
	 * To make this action control a mounted `LayoutWithPanel`, provide `state.panelWidth` as its
	 * `panelWidth` and this action as its `onPanelWidthChange` callback, with
	 * `platform-dst-panel-system-width-persistence` enabled.
	 */
	setPanelWidth: (width: number) => void;
};

/**
 * Props for the PanelProvider component
 */
export type PanelProviderProps = {
	/**
	 * Child components that will have access to the panel manager
	 */
	children: ReactNode;
	/**
	 * Optional initial state for the panel system.
	 *
	 * `initialState.panelWidth` can seed the provider width during SSR. `LayoutWithPanel` mirrors
	 * its requested width into provider state after mounting. CSS may constrain the visible width
	 * without changing the requested value.
	 */
	initialState?: Partial<PanelSystemState>;
};

/**
 * Pure reducer function handling panel, preloaded entry point, and requested-width state transitions.
 *
 * LayoutWithPanel mirrors its requested width here. To apply provider width changes to an
 * already-mounted layout, connect its panelWidth and onPanelWidthChange props to the provider
 * with platform-dst-panel-system-width-persistence enabled.
 */
function panelReducer(state: PanelSystemState, action: PanelAction): PanelSystemState {
	switch (action.type) {
		case 'PANEL_OPEN': {
			const { instanceId, panelContent, params } = action.payload;
			const newPanel: Panel = {
				instanceId,
				panelContent,
				params: params || {},
			};

			return {
				...state,
				activePanel: newPanel,
			};
		}

		case 'PANEL_OPEN_ENTRYPOINT': {
			const { instanceId, entryPoint, params, entryPointReference } = action.payload;
			const newPanel: EntryPointPanel = {
				instanceId,
				entryPoint,
				params: params || {},
				entryPointReference,
			};

			return {
				...state,
				activePanel: newPanel,
			};
		}

		case 'PANEL_CLOSE': {
			const { instanceId } = action.payload;
			// A delayed close from replaced content must not close the new panel.
			if (state.activePanel?.instanceId !== instanceId) {
				return state;
			}
			return {
				...state,
				activePanel: null,
			};
		}

		case 'PANEL_SAVE_ENTRYPOINT': {
			const { instanceId, entryPointReference } = action.payload;
			const filtered = state.preloadedPanels.filter((p) => p.instanceId !== instanceId);
			return {
				...state,
				preloadedPanels: [...filtered, { instanceId, entryPointReference }],
			};
		}

		case 'PANEL_SET_WIDTH': {
			const { width } = action.payload;
			if (state.panelWidth === width) {
				return state;
			}
			return {
				...state,
				panelWidth: width,
			};
		}

		default:
			return state;
	}
}

/**
 * PanelProvider component that wraps your application to provide panel management context.
 *
 * Uses split contexts for optimal performance:
 * - ManagerContext: Provides stable panel manager functions (never re-renders consumers)
 * - StateContext: Provides panel state (re-renders consumers when state changes)
 *
 * This design prevents unnecessary re-renders of components that only use the manager.
 *
 * @example
 * ```tsx
 * import { PanelProvider, usePanelManager } from '@atlassian/panel-system';
 *
 * export function App() {
 *   return (
 *     <PanelProvider>
 *       <YourComponent />
 *     </PanelProvider>
 *   );
 * }
 *
 * function YourComponent() {
 *   const { state, manager } = usePanelManager();
 *   return (
 *     <button onClick={() => manager.openPanel({ instanceId: 'id-1', entryPoint })}>
 *       Open Panel
 *     </button>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function PanelProvider({ children, initialState }: PanelProviderProps): React.JSX.Element {
	const [state, dispatch] = useReducer(panelReducer, {
		activePanel: null,
		preloadedPanels: [],
		panelWidth: fg('platform-dst-chat-panel-layout')
			? DEFAULT_PANEL_WIDTH_WITH_CHAT_PANEL_LAYOUT
			: DEFAULT_PANEL_WIDTH,
		...initialState,
	});

	// Create stable manager object with memoization
	// This object reference never changes, so ManagerContext consumers never re-render
	const manager = useMemo(() => {
		return {
			/**
			 * Opens a panel with either an entry point or React content.
			 * Always replaces the active panel, including when the instance ID is unchanged.
			 *
			 * **Note:** This is a low-level API. For most use cases, prefer using the `Trigger` component,
			 * which handles entry point loading and ID generation automatically.
			 *
			 * **ID Generation:** The instanceId must be provided. For best practices, use the `Trigger`
			 * component's instanceId (either provided or auto-generated) rather than generating IDs
			 * at the manager level. This keeps ID management co-located with the trigger.
			 *
			 * @param options - Instance ID, optional params, and either entryPoint (with an optional
			 * preloaded entryPointReference) or panelContent.
			 */
			openPanel: (options: OpenPanelOptions) => {
				const { instanceId, params } = options;
				if (options.entryPoint !== undefined) {
					dispatch({
						type: 'PANEL_OPEN_ENTRYPOINT',
						payload: {
							instanceId,
							params,
							entryPoint: options.entryPoint,
							entryPointReference: options.entryPointReference,
						},
					});
				} else {
					dispatch({
						type: 'PANEL_OPEN',
						payload: { instanceId, params, panelContent: options.panelContent },
					});
				}
			},

			closePanel: (instanceId: string) => {
				dispatch({
					type: 'PANEL_CLOSE',
					payload: {
						instanceId,
					},
				});
			},

			/**
			 * Saves a preloaded EntryPoint reference for a given panel instance.
			 * Useful for mixing Trigger and programmatic panel opening.
			 *
			 * @param instanceId - Unique identifier for this panel instance
			 * @param entryPointReference - Preloaded EntryPoint reference
			 */
			saveEntryPointReference: (instanceId: string, entryPointReference: any) => {
				dispatch({
					type: 'PANEL_SAVE_ENTRYPOINT',
					payload: {
						instanceId,
						entryPointReference,
					},
				});
			},

			setPanelWidth: (width: number) => {
				dispatch({
					type: 'PANEL_SET_WIDTH',
					payload: {
						width,
					},
				});
			},
		};
	}, []);

	return (
		<ManagerContext.Provider value={manager}>
			<StateContext.Provider value={state}>{children}</StateContext.Provider>
		</ManagerContext.Provider>
	);
}

/**
 * Hook to access the panel manager actions from context.
 * Must be called within a PanelProvider.
 *
 * The manager object is stable and memoized, so using this hook alone
 * won't cause re-renders when state changes.
 *
 * @throws {Error} If called outside of a PanelProvider
 * @internal
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function usePanelActionsInternal(): PanelManager {
	const manager = useContext(ManagerContext);

	if (!manager) {
		throw new Error(
			'usePanelActionsInternal must be used within a <PanelProvider>. ' +
				'Wrap your app with: <PanelProvider><App /></PanelProvider>',
		);
	}

	return manager;
}
